import { useEffect, useState } from 'react';
import { ShieldIcon, KeyIcon, UserCheck, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { canManageRoles } from '../../lib/permissions';
import { DatabaseUser } from '../../types/database';

interface UserRole {
  id: string;
  role: 'view_only' | 'administrator' | 'superadmin';
  email: string;
  full_name: string;
}

const AccessManagement = () => {
  const { user, userRole: authUserRole } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const hasRoleManagementAccess = canManageRoles(authUserRole);

  // Fetch all users and their roles
  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null); // Clear any previous errors

      console.log('Fetching users with role:', authUserRole);
      const { data: users, error } = await supabase.rpc('get_users') as {
        data: DatabaseUser[] | null;
        error: Error | null;
      };

      if (error) {
        console.error('Supabase RPC error:', error);
        throw error;
      }

      if (!users) {
        console.log('No users found');
        setUserRoles([]);
        return;
      }

      console.log('Users data received:', users);
      const userRolesData = users.map(user => ({
        id: user.id,
        email: user.email,
        role: user.role,
        full_name: user.full_name || user.email.split('@')[0] || 'Unknown'
      }));

      console.log('Processed user roles:', userRolesData);
      setUserRoles(userRolesData);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId: string, newRole: 'view_only' | 'administrator' | 'superadmin') => {
    try {
      setLoading(true);
      const { error } = await supabase.rpc('update_user_role', {
        target_user_id: userId,
        new_role: newRole
      });

      if (error) throw error;

      // Refresh the user list to get updated data
      await fetchUsers();
    } catch (err) {
      console.error('Error updating role:', err);
      setError(err instanceof Error ? err.message : 'Failed to update role');
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadgeClasses = (role: string) => {
    switch (role) {
      case 'superadmin':
        return 'bg-purple-100 text-purple-800';
      case 'administrator':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!hasRoleManagementAccess) {
    return (
      <div className="rounded-lg bg-yellow-50 p-4">
        <div className="flex">
          <AlertCircle className="h-5 w-5 text-yellow-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">
              Access Restricted
            </h3>
            <p className="mt-2 text-sm text-yellow-700">
              You need superadmin privileges to manage user roles.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center">
        <ShieldIcon className="h-6 w-6 text-blue-500 mr-2" />
        <h2 className="text-xl font-medium text-gray-800">Access Management</h2>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="mt-2 text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 border-b border-gray-200">
          <div className="flex items-center">
            <KeyIcon className="h-5 w-5 text-gray-400 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">User Roles</h3>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Current Role
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {userRoles.map((userRole) => (
                <tr key={userRole.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <UserCheck className="h-4 w-4 text-blue-600" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {userRole.full_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {userRole.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadgeClasses(userRole.role)}`}>
                      {userRole.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <select
                      value={userRole.role}
                      onChange={(e) => handleRoleChange(userRole.id, e.target.value as 'view_only' | 'administrator' | 'superadmin')}
                      className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                      disabled={loading || userRole.id === user?.id} // Can't change own role
                    >
                      <option value="view_only">View Only</option>
                      <option value="administrator">Administrator</option>
                      <option value="superadmin">Superadmin</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AccessManagement;
